import * as React from 'react';
import { CustomInputLabel, CustomModifiedMarker } from '..';
import './CustomDatePicker.less';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';

type CustomProps = {
    label: string;
    labelTooltip?: string;
    required?: boolean;
    topRightNode?: React.ReactNode;
    containerStyle?: React.CSSProperties;
    hasError?: boolean;
    showModifiedMarker?: boolean;
    getContainerRef?: (containerRef: React.RefObject<HTMLDivElement>) => void;
};

type AntdDatePickerProps = React.ComponentProps<typeof DatePicker>;

type DateValue = Date | Moment | string | number | null;

export type CustomDatePickerProps = CustomProps &
    Omit<AntdDatePickerProps, 'value' | 'defaultValue' | 'onChange'> & {
        value?: DateValue;
        defaultValue?: DateValue;
        onChange?: (value: Date | null, dateString: string) => void;
    };

const toMomentValue = (value: DateValue | undefined): Moment | null | undefined => {
    if (value === undefined) {
        return undefined;
    }

    if (value === null || value === '') {
        return null;
    }

    if (moment.isMoment(value)) {
        return value;
    }

    if (value instanceof Date) {
        return moment(value);
    }

    const parsedValue = moment(value);
    return parsedValue.isValid() ? parsedValue : null;
};

const CustomDatePicker: React.FC<CustomDatePickerProps> = (props: CustomDatePickerProps) => {
    const {
        label,
        labelTooltip,
        required,
        topRightNode,
        containerStyle,
        hasError,
        showModifiedMarker,
        getContainerRef,
        value,
        defaultValue,
        onChange,
        ...datePickerProps
    } = props;

    const datePickerValue = React.useMemo(() => toMomentValue(value), [value]);
    const datePickerDefaultValue = React.useMemo(() => {
        const parsedDefaultValue = toMomentValue(defaultValue);
        return parsedDefaultValue ?? undefined;
    }, [defaultValue]);

    const handleChange = React.useCallback(
        (date: Moment | null, dateString: string) => {
            onChange?.(date ? date.toDate() : null, dateString);
        },
        [onChange]
    );

    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (getContainerRef) {
            getContainerRef(containerRef);
        }
    }, [getContainerRef]);

    const getContainerClassName = () => {
        let className = 'custom-date-picker-container';

        if (hasError) {
            className += ' has-error';
        }

        return className;
    };

    return (
        <div ref={containerRef} className={getContainerClassName()} style={containerStyle}>
            {!!topRightNode && <div className="top-right-node-container">{topRightNode}</div>}

            <DatePicker
                className={`custom-date-picker ${value ? 'not-empty' : ''}`}
                value={datePickerValue}
                defaultValue={datePickerDefaultValue}
                onChange={handleChange}
                {...datePickerProps}
            />

            <CustomInputLabel label={label} labelTooltip={labelTooltip} required={required} />

            {showModifiedMarker && <CustomModifiedMarker />}
        </div>
    );
};

export default CustomDatePicker;
